import { useEffect, useRef, useState } from 'react';
import type * as Leaflet from 'leaflet';

/**
 * A map with one pin, for saying exactly where an address is.
 *
 * Coordinates were two text boxes nobody could fill without opening a map in
 * another tab and copying numbers across. Here the operator searches (the
 * place search above it), clicks the map, or drags the pin, and the numbers
 * are kept behind the form.
 *
 * Leaflet over OpenStreetMap tiles: free, no key to provision or leak, and
 * nothing billed per map load. The tile servers ask for attribution (shown in
 * the corner) and for modest use — one operator placing a pin is that.
 * Leaflet is imported on first render, so a screen without a map never
 * downloads it. If it fails to load, the caller falls back to plain inputs.
 */

/** Roughly the middle of India, when there is no point yet. */
const INDIA: [number, number] = [22.5, 79];

const round = (n: number) => Math.round(n * 1e6) / 1e6;

/** A drawn pin: Leaflet's default marker image does not survive bundling. */
const PIN_HTML = `<svg width="28" height="40" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14 0C6.3 0 0 6.2 0 13.9 0 24.3 14 40 14 40s14-15.7 14-26.1C28 6.2 21.7 0 14 0z" fill="#4338ca"/>
  <circle cx="14" cy="14" r="5.5" fill="#fff"/></svg>`;

export function MapPin({ latitude, longitude, onChange, onUnavailable, disabled = false }: {
  latitude: number | null;
  longitude: number | null;
  onChange: (lat: number, lng: number) => void;
  /** Called once if the map cannot be shown, so the caller can offer inputs. */
  onUnavailable?: () => void;
  disabled?: boolean;
}) {
  const el = useRef<HTMLDivElement>(null);
  const L = useRef<typeof Leaflet | null>(null);
  const map = useRef<Leaflet.Map | null>(null);
  const marker = useRef<Leaflet.Marker | null>(null);
  const changed = useRef(onChange);
  changed.current = onChange;
  const [failed, setFailed] = useState(false);
  const has = latitude != null && longitude != null && Number.isFinite(latitude) && Number.isFinite(longitude);
  const at = useRef<[number, number] | null>(has ? [latitude!, longitude!] : null);
  at.current = has ? [latitude!, longitude!] : null;

  /** Puts the pin at `p`, creating it the first time. */
  const pinAt = (p: [number, number]) => {
    const lib = L.current; const m = map.current;
    if (!lib || !m) return;
    if (marker.current) { marker.current.setLatLng(p); return; }
    marker.current = lib.marker(p, {
      icon: lib.divIcon({ html: PIN_HTML, className: '', iconSize: [28, 40], iconAnchor: [14, 40] }),
      draggable: !disabled, keyboard: false,
    }).addTo(m);
    marker.current.on('dragend', () => {
      const ll = marker.current!.getLatLng();
      changed.current(round(ll.lat), round(ll.lng));
    });
  };

  useEffect(() => {
    let live = true;
    Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')]).then(([mod]) => {
      if (!live || !el.current) return;
      const lib = ((mod as unknown as { default?: typeof Leaflet }).default ?? mod) as typeof Leaflet;
      L.current = lib;
      const start = at.current;
      const m = lib.map(el.current, { scrollWheelZoom: false }).setView(start ?? INDIA, start ? 16 : 4);
      lib.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>',
      }).addTo(m);
      map.current = m;
      if (start) pinAt(start);
      if (!disabled) {
        m.on('click', (e: Leaflet.LeafletMouseEvent) => {
          const p: [number, number] = [round(e.latlng.lat), round(e.latlng.lng)];
          pinAt(p);
          changed.current(p[0], p[1]);
        });
      }
    }).catch(() => {
      if (!live) return;
      setFailed(true);
      onUnavailable?.();
    });
    return () => {
      live = false;
      map.current?.remove();
      map.current = null;
      marker.current = null;
    };
    // The map is built once; later coordinate changes move it below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A point chosen from the place search arrives as props: follow it. A
  // removed pin leaves the map where it is.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (!has) {
      marker.current?.remove();
      marker.current = null;
      return;
    }
    pinAt([latitude!, longitude!]);
    m.setView([latitude!, longitude!], Math.max(m.getZoom(), 16));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, has]);

  if (failed) return null;
  return (
    <div
      ref={el}
      role="application"
      aria-label="Map — click or drag the pin to set the exact location"
      className="relative z-0 h-60 w-full overflow-hidden rounded-card border border-neutral-200 bg-neutral-100 max-md:h-52"
    />
  );
}
